import React, { useState } from 'react'
import { Modal, Form, Input, Button, Alert, Space, Typography, message } from 'antd'
import { useSelector, useDispatch, batch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash, faShield, faSignIn } from '@fortawesome/free-solid-svg-icons'

import { authenticateWithPassphrase } from '../utils/passphraseUtils'
import authReducer from '../utils/reducer'
import coreReducer from '../../../core/utils/reducer'
import AuthEnums from '../utils/enums'
import { handleError } from '../../lib/utils'

const { Title, Text } = Typography

const PassphraseLoginModal = ({ visible, onCancel, identifier, onSuccess }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const coreState = useSelector((state) => state.core)
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [showPassphrase, setShowPassphrase] = useState(false)
  const [passphrase, setPassphrase] = useState('')

  const handleSubmit = async () => {
    try {
      await form.validateFields()

      if (!passphrase) {
        message.error('Please enter your passphrase')
        return
      }

      setLoading(true)

      // Authenticate with passphrase
      const user = await authenticateWithPassphrase(identifier, passphrase)

      if (!user) {
        throw new Error('Authentication failed')
      }

      // User authentication successful - let the parent component handle the rest

      message.success('Successfully logged in with backup passphrase!')

      // Clear form
      form.resetFields()
      setPassphrase('')

      // Call success callback which will handle the rest of the auth flow
      if (onSuccess) {
        onSuccess(user)
      }
    } catch (error) {
      console.error('Error authenticating with passphrase:', error)
      message.error(handleError(error, true))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setPassphrase('')

    // Reset passphrase login mode
    dispatch(authReducer.actions.setPassphraseLoginMode(false))

    if (onCancel) {
      onCancel()
    }
  }

  const formatIdentifier = (id) => {
    if (!id) return ''

    // If it looks like a phone number (starts with + or is all digits)
    if (id.startsWith('+') || /^\d+$/.test(id)) {
      // Mask phone number: +27821234567 -> +27***4567
      if (id.length > 6) {
        return id.substring(0, 3) + '***' + id.substring(id.length - 4)
      }
      return id
    }

    // If it looks like an email
    if (id.includes('@')) {
      const [username, domain] = id.split('@')
      if (username.length > 2) {
        return username.substring(0, 2) + '***@' + domain
      }
      return id
    }

    return id
  }

  return (
    <Modal
      title={
        <Space>
          <FontAwesomeIcon icon={faShield} style={{ color: '#1890ff' }} />
          <span>Backup Passphrase Login</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={450}
      maskClosable={false}
      closable={!loading}
    >
      <div style={{ marginBottom: 20 }}>
        <Alert
          message='Alternative Login Method'
          description={
            <div>
              <p>Enter your backup passphrase to access your account.</p>
              <p>
                <Text strong>Account:</Text> {formatIdentifier(identifier)}
              </p>
            </div>
          }
          type='info'
          showIcon
          style={{ marginBottom: 16 }}
        />
      </div>

      <Form form={form} layout='vertical' onFinish={handleSubmit} autoComplete='off'>
        <Form.Item
          label='Backup Passphrase'
          name='passphrase'
          rules={[{ required: true, message: 'Please enter your backup passphrase' }]}
        >
          <Input.Password
            placeholder='Enter your backup passphrase'
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            iconRender={(visible) => (
              <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} onClick={() => setShowPassphrase(!showPassphrase)} />
            )}
            visibilityToggle={{
              visible: showPassphrase,
              onVisibleChange: setShowPassphrase
            }}
            onPressEnter={handleSubmit}
          />
        </Form.Item>

        <div style={{ textAlign: 'right', marginTop: 20 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              type='primary'
              htmlType='submit'
              loading={loading}
              disabled={!passphrase}
              icon={<FontAwesomeIcon icon={faSignIn} />}
            >
              Sign In
            </Button>
          </Space>
        </div>
      </Form>

      <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6f6f6', borderRadius: 6 }}>
        <Text type='secondary' style={{ fontSize: '12px' }}>
          <strong>Security Tip:</strong> Your passphrase is case-sensitive and must match exactly as you set it up. If
          you've forgotten your passphrase, please contact support.
        </Text>
      </div>
    </Modal>
  )
}

export default PassphraseLoginModal
